<template>
    <div class="relatorio-analitico">
        <access-denied v-if="accessDenied" :title="'gerencial-integrado'" />
        <div id="main" v-else>
            <loading :isLoading="isLoading" />
            <div class="page">
                <div class="vertical-flow">
                    <div class="listing-header">
                        <div class="title-breadcrumb">
                            <h1 class="title">
                                {{ $t('gerencial-integrado') }}
                            </h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs" v-if="cameFrom == 'route'">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{ $t('vendas') }}</a>
                                    </li>
                                    <li>
                                        <a class="a-inativo">{{ $t('relatorio-integrado-erp') }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{ $t('gerencial-integrado') }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="header-buttons">
                            <button
                                v-if="cameFrom != 'route'"
                                class="delete-btn"
                                type="button"
                                tag="button"
                                @click="$emit('voltar', true)"
                            >
                                {{ $t('voltar') }}
                            </button>
                        </div>
                    </div>

                    <!-- PESQUISA E FILTROS -->
                    <div>
                        <div class="is-flex gap-3">
                            <search-bar
                                class="is-flex-grow-2"
                                :onSearch="debouncedOnSearch"
                                :tooltipLabel="getSearchableFields()"
                                ref="search"
                            />

                            <vss-select
                                class="is-flex-grow-1"
                                ref="situacaoInput"
                                title="situacao"
                                :options="situacaoSelect"
                                :mlLabels="true"
                                @input="onSituacaoChanged"
                                v-model="situacaoSelected"
                                :height="40"
                            />
                            <vss-select
                                class="is-flex-grow-2"
                                title="objetivo-venda"
                                :value="objetivoVendaSelected"
                                :options="objetivoVendaList"
                                :height="40"
                                :loading="objetivoVendaLoading"
                                @search="carregarObjetivoVenda"
                                @input="updateObjetivoVenda"
                                label="descricao"
                                no-clearfix
                                searchable
                                required
                            />
                            <b-checkbox
                                class="mt-3"
                                v-model="filtros.agrupado"
                                type="is-primary"
                                @input="checkAgrupado"
                            >
                                {{ $t('agrupado') }}
                            </b-checkbox>
                            <vss-select
                                class="is-flex-grow-2"
                                title="colaborador"
                                ref="colaboradorSelect"
                                :value="colaboradorSelected"
                                :options="colaboradorList"
                                :height="40"
                                :loading="colaboradorLoading"
                                @search="carregarColaboradores"
                                @input="updateColaborador"
                                label="nome"
                                :closeOnSelect="false"
                                no-clearfix
                                multiple
                                clearable
                                searchable
                                :disabled="filtros.agrupado"
                            >
                                <template #multiple-selected="{ selected }">
                                    ({{ selected && selected.length > 0 ? selected.length : $t('todos') }})
                                </template>
                            </vss-select>
                            <div>
                                <b-button
                                    class="mt-5"
                                    type="is-primary"
                                    icon-right="filter"
                                    @click="showFilter = !showFilter"
                                    v-tippy
                                    :title="$t('filtros')"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="objetivoVendaItemList"
                            @sort="sortTable"
                            :custom-is-checked="(selected, row) => selected.id === row.id"
                            backend-sorting
                            hoverable
                            bordered
                        >
                            <b-table-column
                                v-for="(field, i) in fields"
                                :key="i"
                                :field="field.key"
                                :label="$t(field.key)"
                                :sortable="field.sortable"
                                :width="field.width"
                                :visible="field.visible"
                            >
                                <template v-slot="props">
                                    <slot name="table-column" :props="props" :field="field">
                                        <div v-if="field.key === 'diferenca'">
                                            <span
                                                :class="{
                                                    'is-text-color-success': props.row[field.key] > 0,
                                                    'is-text-color-danger': props.row[field.key] < 0,
                                                }"
                                            >
                                                {{ !isNull(props.row[field.key]) ? formatValueField(props.row[field.key]) : props.row[field.key] }}
                                            </span>
                                        </div>
                                        <div v-else-if="field.key === 'objetivo' || field.key === 'realizado'">
                                            <span>
                                                {{ !isNull(props.row[field.key]) ? formatValueField(props.row[field.key]) : props.row[field.key] }}
                                            </span>
                                        </div>
                                        <div v-else-if="field.key === 'percentual'">
                                            <span
                                                :class="{
                                                    'is-text-color-success': props.row[field.key] > 100
                                                }"
                                            >
                                                {{ !isNull(props.row[field.key]) ? parseAsPercent(props.row[field.key]) : props.row[field.key] }}
                                            </span>
                                        </div>
                                        <div v-else>
                                            {{ isNull(props.row[field.key]) ? '---' : props.row[field.key] }}
                                        </div>
                                    </slot>
                                </template>
                            </b-table-column>
                        </b-table>

                        <div class="totalizador totalizador-columns">
                            <div>
                                <label>{{ $t('total-objetivo') }}:</label>
                                {{ formatValueField(totalObjetivo) }}
                            </div>
                            <div>
                                <label>{{ $t('total-realizado') }}:</label>
                                {{ formatValueField(totalRealizado) }}
                            </div>
                            <div class="is-flex is-flex-vcenter g-2">
                                <label>{{ $t('total-diferenca') }}:</label>
                                <span 
                                    :class="{
                                        'is-text-color-success': totalDiferenca > 0,
                                        'is-text-color-danger': totalDiferenca < 0,
                                    }"
                                >
                                    {{ formatValueField(totalDiferenca) }}
                                </span>
                                <b-icon
                                    v-if="totalDiferenca != 0"
                                    :icon="totalDiferenca > 0 ? 'trending-up' : 'trending-down'"
                                />
                            </div>
                        </div>

                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>

            <lateral-content
                :isLoading="isLoading"
                v-model="showFilter"
                footer
            >
                <template v-slot:footer-right>
                    <b-button
                        type="is-warning is-silver"
                        icon-right="backspace"
                        @click="onClearFilters()"
                        class="mr-2"
                        :disabled="isLoading"
                    >
                        {{ $t('limpar') }} {{ $t('filtros') }}
                    </b-button>
                    <b-button
                        type="is-primary"
                        icon-right="filter"
                        @click="
                            carregarDados();
                            showFilter = false;
                        "
                        :disabled="isLoading"
                    >
                        {{ $t('filtrar') }}
                    </b-button>
                </template>
                <RelatorioGerencialIntegradoFiltro
                    ref="filtroRef"
                    :filtersReset="filtersReset"
                    @update-filtro="updateFiltro"
                />
            </lateral-content>
        </div>
    </div>
</template>

<script>
import RelatorioAnaliticoComponentList from '@/components/shared/tela/RelatorioAnaliticoComponentList.vue';
import RelatorioGerencialIntegradoFiltro from '@/components/vendas/relatorio-integrado-erp/gerencial-integrado/RelatorioGerencialIntegradoFiltro.vue';

export default {
    components: {
        RelatorioAnaliticoComponentList,
        RelatorioGerencialIntegradoFiltro
    },
    props: {
        cameFrom: {
            type: String,
            default: 'route',
        },
        idFrom: {
            type: Number,
            default: 0,
        },
        reloadData: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            permissao: null,
            accessDenied: false,
            debouncedOnSearch: this.debounce(this.onSearch, 500),
            showFilter: false,
            app_modulo: 'vendas',
            app_submodulo: 'relatorio-integrado-erp',
            app_tela: 'relatorio-ven-gerencial-integrado',
            objetivoVendaItemList: [],

            fields: [
                { key: 'empresa',  width: 300, sortable: true, visible: true },
                { key: 'colaborador',  width: 300, sortable: true, visible: true },
                { key: 'cidade',  width: 200, sortable: true, visible: true },
                { key: 'cliente',  width: 400, sortable: true, visible: true },
                { key: 'grupoProduto',  width: 200, sortable: true, visible: true },
                { key: 'subGrupoProduto',  width: 200, sortable: true, visible: true },
                { key: 'categoria',  width: 200, sortable: true, visible: true },
                { key: 'familia',  width: 200, sortable: true, visible: true },
                { key: 'produto',  width: 400, sortable: true, visible: true },
                { key: 'unidadeMedida', width: 200, sortable: true, visible: true },
                { key: 'objetivo',  width: 150, sortable: true, visible: true },
                { key: 'realizado',  width: 150, sortable: true, visible: true },
                { key: 'diferenca',  width: 150, sortable: true, visible: true },
                { key: 'percentual',  width: 100, sortable: true, visible: true },
            ],

            filtros: {
                idCategoriaList: [],
                idCidadeList: [],
                idClienteList: [],
                idEmpresaList: [],
                idFamiliaList: [],
                idGrupoList: [],
                idProdutoList: [],
                idSubGrupoProdutoList: [],
                idUnidadeMedidaList: [],
                idVendedorList: [],
                objetivoVenda: {},
                agrupado: false,
                situacao: 1,
            },

            totalObjetivo: 0,
            totalRealizado: 0,
            totalDiferenca: 0,

            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
                orderColumn: 'id',
                orderDirection: 'asc',
                queryFilter: '.*',
            },

            situacaoSelected: { id: 1, label: 'ativos' },
            situacaoSelect: [
                { id: 2, label: 'todos' },
                { id: 1, label: 'ativos' },
                { id: 0, label: 'inativos' },
            ],

            objetivoVendaList: [],
            objetivoVendaSelected: [],
            objetivoVendaLoading: false,

            colaboradorList: [],
            colaboradorSelected: [],
            colaboradorLoading: false,

            isLoading: false,

            isEmpty: true,
            filtersReset: false,

            isDataStarted: false,
        };
    },
    watch: {
        reloadData(val) {
            if (val) this.carregarDados(this.pagination.currentPage);
        },
    },
    created() {
        this.permissao = this.hasPermissionAccess(this.app_modulo, this.app_submodulo, this.app_tela);
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];

        if (!this.permissao.read) {
            this.accessDenied = true;
        } else {
            this.carregarObjetivoVenda();
        }
    },
    methods: {
        formatValueField(value) {
            if(!this.objetivoVendaSelected) return;

            const tipoAnalise = this.objetivoVendaSelected.tipoAnalise;

            if(tipoAnalise) return this.getDecimal(value) 

            return this.parseAsCurrency(value)
        },
        getSearchableFields() {
            if(!this.objetivoVendaSelected) return;
        
            const searchableFields = [];

            if(this.objetivoVendaSelected.habilitaEmpresa)
                searchableFields.push(this.$t('empresa'));

            if(this.objetivoVendaSelected.habilitaUsuario)
                searchableFields.push(this.$t('usuario'));

            if(this.objetivoVendaSelected.habilitaCliente)
                searchableFields.push(this.$t('cliente'));

            if(this.objetivoVendaSelected.habilitaCidade)
                searchableFields.push(this.$t('cidade'));

            if(this.objetivoVendaSelected.habilitaGrupoProduto)
                searchableFields.push(this.$t('grupoProduto'));

            if(this.objetivoVendaSelected.habilitaSubgrupoProduto)
                searchableFields.push(this.$t('subGrupoProduto'));

            if(this.objetivoVendaSelected.habilitaCategoria)
                searchableFields.push(this.$t('categoria'));

            if(this.objetivoVendaSelected.habilitaFamilia)
                searchableFields.push(this.$t('familia'));

            if(this.objetivoVendaSelected.habilitaProduto)
                searchableFields.push(this.$t('produto'));

            if(this.objetivoVendaSelected.habilitaUnidadeMedida)
                searchableFields.push(this.$t('unidadeMedida'));

            const searchableFieldsFormatted = searchableFields.length > 0 ? searchableFields.join(", ") : this.$t("nenhum")

            return `${this.$t("msg-campo-busca")}${searchableFieldsFormatted}`
        },
        checkAgrupado(value) {
            this.filtros.agrupado = value

            if(value) {
                this.colaboradorSelected = []
                this.filtros.idVendedorList = []
            }

            const colaboradorField = this.fields.find(field => field.key === 'colaborador')

            colaboradorField.visible = !value && this.objetivoVendaSelected && this.objetivoVendaSelected.habilitaUsuario;

            this.carregarDados()
        },
        onSituacaoChanged(newValue) {
            this.filtros.situacao = newValue.id;

            this.carregarDados();
        },
        onSearch(value) {
            this.pagination.queryFilter = value;
            this.carregarDados();
        },
        carregarDados(currentPage = 1) {
            if(!this.objetivoVendaSelected) return;
            
            if (currentPage == 1 && this.pagination.currentPage != 1) {
                this.pagination.resetPagination = true;
            }

            this.isLoading = true;
            this.$axios
                .post(`ven/relatorio/objetivo-venda/list.json`, this.filtros, {
                    params: {
                        column: this.pagination.orderColumn,
                        order: this.pagination.orderDirection,
                        page: currentPage - 1,
                        limit: this.pagination.perPage,
                        search: this.pagination.queryFilter,
                    },
                })
                .then((response) => {
                    const responseContent = response.data.content;

                    this.isEmpty = responseContent.page.totalElements == 0;
                    this.pagination.totalRows = responseContent.page.totalElements;

                    this.totalObjetivo = responseContent.totalObjetivo;
                    this.totalRealizado = responseContent.totalRealizado;
                    this.totalDiferenca = responseContent.totalDiferenca;

                    return responseContent.page.content;
                })
                .then((mappedContent) => {
                        this.objetivoVendaItemList = mappedContent;
                })
                .catch((exception) => {
                    console.error('Erro ao carregar rupturas.', exception);
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-dados-geral');
                })
                .finally((_) => {
                    this.pagination.resetPagination = false;
                    this.isDataStarted = true;
                    this.isLoading = false;
                });
        },
        carregarColaboradores(search = '') {
            this.colaboradorLoading = true;

            this.$axios
                .get(`/employees/list/associated.json?search=${search}`)
                .then((response) => {
                    if (response.status != 200) {
                        console.error(
                            `[OBJETIVO DE VENDA] Erro status ${r.status} ao carregar colaboradores.`
                        );
                        console.error(`[OBJETIVO DE VENDA] Mensagem: ${r.message}`);
                    } else {
                        this.colaboradorList = response.data.content;
                    }
                })
                .catch((e) => {
                    this.empresaList = [];
                    console.error('[OBJETIVO DE VENDA] Erro ao carregar colaboradores.', e);
                })
                .finally((f) => {
                    this.colaboradorLoading = false;
                });
        },
        updateColaborador(colaborador) {
            this.colaboradorSelected = colaborador;

            this.filtros.idVendedorList =
                this.colaboradorSelected.length == 0
                    ? []
                    : this.colaboradorSelected.map((e) => e.id);

            this.carregarDados();
        },
        carregarObjetivoVenda(search = '') {
            this.objetivoVendaLoading = true;

            this.$axios
                .get(`/objetivo-venda/getcombo.json`, {
                    params: {
                        limit: 200,
                        situacao: 1,
                        param: search
                    }
                })
                .then((response) => {
                    if (response.status != 200) {
                        console.error(
                            `[OBJETIVO DE VENDA] Erro status ${r.status} ao carregar objetivos de venda.`
                        );
                        console.error(`[OBJETIVO DE VENDA] Mensagem: ${r.message}`);
                    } else {
                        this.objetivoVendaList = response.data.content;
                        this.updateObjetivoVenda(this.objetivoVendaList.at(0));
                    }
                })
                .catch((e) => {
                    this.empresaList = [];
                    console.error('[OBJETIVO DE VENDA] Erro ao carregar objetivos de venda.', e);
                })
                .finally((f) => {
                    this.objetivoVendaLoading = false;
                });
        },
        updateObjetivoVenda(objetivoVenda) {
            this.objetivoVendaSelected = objetivoVenda;

            this.filtros.objetivoVenda = objetivoVenda;

            this.setFieldsFromObjetivoVenda(objetivoVenda)

            this.carregarDados();
        },
        clearFilter() {
            this.filtersReset = true;

            this.pagination.queryFilter = '.*';
            this.$refs.search.cleanSearch();
            
            this.filtros.idEmpresaList = [];
            this.filtros.idCategoriaList = [];
            this.filtros.idCidadeList = [];
            this.filtros.idClienteList = [];
            this.filtros.idEmpresaList = [];
            this.filtros.idFamiliaList = [];
            this.filtros.idGrupoList = [];
            this.filtros.idLpsList = [];
            this.filtros.idProdutoList = [];
            this.filtros.idSubGrupoProdutoList = [];
            this.filtros.idUnidadeMedidaList = [];
            this.filtros.idVendedorList = [];
            this.filtros.agrupado = false;

            this.filtros.situacao = 2;
            this.situacaoSelected = { id: 2, label: 'todos' };

            this.colaboradorSelected = [];

            this.carregarDados();


            setTimeout(() => {                
                this.filtersReset = false;
            }, 1000);

            this.showFilter = false;
        },
        updateFiltro(filtro) {
            this.filtros[filtro.nomeFiltro] = filtro.valor;
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        sortTable(field, order) {
            this.pagination.orderColumn = field;
            this.pagination.orderDirection = order;
            this.carregarDados();
        },
        onClearFilters() {
            this.$root.confirm({
                message: this.$t('msg-confirm-limpar-filtros'),
                onConfirm: () => this.clearFilter(),
            });
        },
        setFieldsFromObjetivoVenda(objetivoVenda) {
            if(!objetivoVenda) return;

            this.fields = [
                { key: 'empresa',  width: 300, sortable: true, visible: objetivoVenda.habilitaEmpresa },
                { key: 'colaborador',  width: 300, sortable: true, visible: !this.filtros.agrupado && objetivoVenda.habilitaUsuario },
                { key: 'cidade',  width: 200, sortable: true, visible: objetivoVenda.habilitaCidade },
                { key: 'cliente',  width: 400, sortable: true, visible: objetivoVenda.habilitaCliente },
                { key: 'grupoProduto',  width: 200, sortable: true, visible: objetivoVenda.habilitaGrupoProduto },
                { key: 'subGrupoProduto',  width: 200, sortable: true, visible: objetivoVenda.habilitaSubgrupoProduto },
                { key: 'categoria',  width: 200, sortable: true, visible: objetivoVenda.habilitaCategoria },
                { key: 'familia',  width: 200, sortable: true, visible: objetivoVenda.habilitaFamilia },
                { key: 'produto',  width: 400, sortable: true, visible: objetivoVenda.habilitaProduto },
                { key: 'unidadeMedida', width: 200, sortable: true, visible: objetivoVenda.habilitaUnidadeMedida },
                { key: 'objetivo',  width: 150, sortable: true, visible: true },
                { key: 'realizado',  width: 150, sortable: true, visible: true },
                { key: 'diferenca',  width: 150, sortable: true, visible: true },
                { key: 'percentual',  width: 100, sortable: true, visible: true }
            ]
        }
    },
};
</script>

<style lang="scss" scoped>
.g-2 {
    gap: 6px;
}

.gap-3 {
    gap: 12px;
}

.relatorio-analitico {
    height: 100%;
}

.ml-custom {
    margin-left: 2rem !important;
}

::v-deep .b-table,
::v-deep .table-wrapper {
    height: 100%;
}

.mt-4-half {
    margin-top: 1.5rem !important
}

#main {
    background-color: $bg-color;
    border-radius: 5px;
    padding: 5px 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;

        .situacao-select div,
        .produtivo-select div {
            height: 40px;
        }
        .contagem {
            position: absolute;
            right: 0;
            top: 0;
            background-color: $green-alert;
            border-radius: 50%;
            line-height: 1;
            min-width: 18px;
            height: 18px;
            margin-top: 0px;
            margin-right: -4px;
            display: flex;
            align-items: center;
            justify-content: center;

            span {
                color: #fff;
                font-weight: bold;
                font-size: 10px;
                padding: 5px;
            }
        }
    }
}

.totalizador {
    background-color: #f9f9f9;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    opacity: 0.7;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    & > div {
        padding: 10px;
        label {
            color: $sovis-black;
            font-size: 1em;
            padding-right: 10px;
        }
        @media screen and (max-width: 750px) {
            padding: 2px 10px;
        }
    }
    @media screen and (max-width: 750px) {
        flex-direction: column;
    }
}
</style>
